import { useQuery } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { get } from '../lib/api.ts'
import { brandStyle } from '../lib/types.ts'
import { LOCALE, t } from '../lib/i18n.ts'

interface Tv {
  condoName: string
  brand: { name: string | null; color: string | null; hasLogo: boolean }
  timezone: string
  notices: { id: string; kind: string; severity: 'info' | 'manutencao' | 'urgente'; title: string; body: string; pinned: boolean; endsAt: string | null }[]
  reservations: { area: string; starts: string; ends: string }[]
  deliveries: { unit: string; n: number }[]
  nextAssembly: { title: string; at: string; mode: string; votingOpen: boolean } | null
  emergencyContacts: { label: string; phone: string }[]
}

const SEV_LABEL = { info: '', manutencao: t('Manutenção'), urgente: t('Urgente') }

/** Kiosk page for the elevator/lobby screen. Large type, no interaction, refreshes every minute. */
export function TvPage() {
  const { token } = useParams({ strict: false }) as { token: string }
  const q = useQuery({ queryKey: ['tv', token], queryFn: () => get<Tv>(`/api/tv/${token}`), refetchInterval: 60_000, retry: 3, staleTime: 30_000 })
  const [now, setNow] = useState(new Date())
  const [page, setPage] = useState(0)
  useEffect(() => {
    const clock = setInterval(() => setNow(new Date()), 15_000)
    const r = setInterval(() => setPage((p) => p + 1), 12_000)
    return () => {
      clearInterval(clock)
      clearInterval(r)
    }
  }, [])
  if (q.isError && !q.data) return <main className="grid min-h-dvh place-items-center bg-tinta text-2xl text-white">{t('Tela desativada ou sem conexão.')}</main>
  if (!q.data) return <main className="grid min-h-dvh place-items-center bg-papel text-2xl">{t('Carregando…')}</main>
  const d = q.data
  const tz = d.timezone
  const perPage = 3
  const pages = Math.max(1, Math.ceil(d.notices.length / perPage))
  const shown = d.notices.slice((page % pages) * perPage, (page % pages) * perPage + perPage)
  const time = (iso: string | Date) => new Intl.DateTimeFormat(LOCALE, { hour: '2-digit', minute: '2-digit', timeZone: tz }).format(new Date(iso))
  const day = (iso: string) => {
    const s = new Intl.DateTimeFormat(LOCALE, { weekday: 'long', day: 'numeric', month: 'long', timeZone: tz }).format(new Date(iso))
    return s.charAt(0).toUpperCase() + s.slice(1)
  }
  const isToday = (iso: string) => day(iso) === day(now.toISOString())
  return (
    <main className="grid min-h-dvh grid-cols-[1fr_minmax(18rem,30%)] bg-papel text-tinta" style={brandStyle(d.brand?.color)}>
      <section className="almaco flex flex-col py-[4vh] pr-[4vw] pl-[6.5rem]">
        <div className="flex items-center gap-[1.2vw]">
          {d.brand?.hasLogo && <img src={`/api/tv/${token}/logo`} alt="" className="h-[4vw] w-auto max-w-[18vw] object-contain" />}
          <p className="text-[1.6vw] font-semibold text-caneta">{d.condoName}{d.brand?.name && d.brand.name !== d.condoName ? <span className="text-tinta-3"> · {d.brand.name}</span> : null}</p>
        </div>
        <h1 className="mt-[1vh] font-doc text-[3.2vw] font-semibold leading-tight">{t('Quadro de avisos')}</h1>
        <div className="mt-[3vh] flex flex-1 flex-col gap-[3vh]">
          {shown.length === 0 ? <p className="font-doc text-[2.2vw] text-tinta-2">{t('Nenhum aviso no momento.')}</p> : shown.map((n) => (
            <article key={n.id}>
              {n.severity !== 'info' && <p className={n.severity === 'urgente' ? 'text-[1.3vw] font-bold text-margem' : 'text-[1.3vw] font-bold text-alerta'}>{SEV_LABEL[n.severity]}</p>}
              <h2 className="font-doc text-[2.6vw] font-semibold leading-snug">{n.title}</h2>
              {n.body && <p className="mt-[0.6vh] line-clamp-4 text-[1.6vw] leading-relaxed text-tinta-2">{n.body.replace(/[#*_`>]/g, '')}</p>}
              {n.endsAt && <p className="mt-[0.4vh] text-[1.2vw] text-tinta-3">{t('Até')}{' '}{day(n.endsAt)}, {time(n.endsAt)}</p>}
            </article>
          ))}
        </div>
        {pages > 1 && <p className="text-[1.1vw] text-tinta-3">{(page % pages) + 1}{' '}{t('de')}{' '}{pages}</p>}
      </section>
      <aside className="flex flex-col gap-[4vh] border-l border-pauta bg-folha p-[3vw]">
        <div>
          <p className="text-[5vw] font-bold leading-none tabular-nums">{time(now)}</p>
          <p className="mt-[1vh] text-[1.5vw] text-tinta-2">{day(now.toISOString())}</p>
        </div>
        {d.nextAssembly && (
          <div>
            <h3 className="text-[1.4vw] font-bold text-caneta">{d.nextAssembly.votingOpen ? t('Votação aberta') : t('Próxima assembleia')}</h3>
            <p className="mt-[0.5vh] text-[1.6vw] font-semibold leading-snug">{d.nextAssembly.title}</p>
            <p className="text-[1.3vw] text-tinta-2">{day(d.nextAssembly.at)}, {time(d.nextAssembly.at)}</p>
            {d.nextAssembly.mode !== 'presencial' && <p className="text-[1.2vw] text-tinta-2">{t('Vote pelo Subsíndico no celular.')}</p>}
          </div>
        )}
        <div>
          <h3 className="text-[1.4vw] font-bold text-caneta">{t('Reservas')}</h3>
          {d.reservations.length === 0 ? <p className="text-[1.3vw] text-tinta-2">{t('Nenhuma hoje ou amanhã.')}</p> : (
            <ul className="mt-[0.5vh]">
              {d.reservations.map((r, i) => <li key={i} className="text-[1.4vw]"><span className="font-semibold">{r.area}</span> <span className="text-tinta-2">{isToday(r.starts) ? 'hoje' : t('amanhã')}, {time(r.starts)}–{time(r.ends)}</span></li>)}
            </ul>
          )}
        </div>
        {d.deliveries?.length > 0 && (
          <div>
            <h3 className="text-[1.4vw] font-bold text-caneta">{t('Encomendas na portaria')}</h3>
            <ul className="mt-[0.5vh] flex flex-wrap gap-x-[1.2vw]">
              {d.deliveries.map((x, i) => <li key={i} className="text-[1.4vw]"><span className="font-semibold">{x.unit}</span>{x.n > 1 && <span className="text-tinta-2"> ({x.n})</span>}</li>)}
            </ul>
          </div>
        )}
        {d.emergencyContacts.length > 0 && (
          <div className="mt-auto">
            <h3 className="text-[1.4vw] font-bold text-margem">{t('Emergência')}</h3>
            <ul>{d.emergencyContacts.map((c, i) => <li key={i} className="text-[1.4vw]"><span className="font-semibold">{c.label}</span> {c.phone}</li>)}</ul>
            <p className="text-[1.2vw] text-tinta-2">{t('Bombeiros 193, SAMU 192, Polícia 190')}</p>
          </div>
        )}
      </aside>
    </main>
  )
}
